import React, { useEffect, useRef, useState } from "react";

/**
 * A highly performant and customizable scroll-triggered fade-in component.
 * Uses IntersectionObserver to trigger CSS transitions when elements enter the viewport.
 */
export default function FadeIn({
  children,
  className = "",
  delay = 0,
  duration = 800,
  direction = "up", // "up" | "down" | "left" | "right" | "none"
  distance = 30, // in pixels
  threshold = 0.05,
  triggerOnce = true,
}) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce && ref.current) {
            observer.unobserve(ref.current);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin: "0px 0px -40px 0px", // Trigger slightly before the element fully enters
      }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef && !triggerOnce) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold, triggerOnce]);

  // Determine starting transform based on direction
  const getDirectionStyle = () => {
    if (!isVisible) {
      switch (direction) {
        case "up":
          return { transform: `translateY(${distance}px)` };
        case "down":
          return { transform: `translateY(-${distance}px)` };
        case "left":
          return { transform: `translateX(${distance}px)` };
        case "right":
          return { transform: `translateX(-${distance}px)` };
        default:
          return {};
      }
    }
    return { transform: "translate(0, 0)" };
  };

  const style = {
    opacity: isVisible ? 1 : 0,
    transitionProperty: "opacity, transform",
    transitionDuration: `${duration}ms`,
    transitionDelay: `${delay}ms`,
    transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)", // Smooth ease-out
    ...getDirectionStyle(),
  };

  return (
    <div ref={ref} style={style} className={className}>
      {children}
    </div>
  );
}
